import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getMyCards, getTheme } from '../utils/cards';
import { useAuth } from '../lib/auth';
import AppHeader from './AppHeader';
import CardDisplay from './CardDisplay';
import ShareTools from './ShareTools';
import Loading from './Loading';
import { errorBox, inputClass, primaryButton, secondaryButton } from './AuthLayout';
import PhoneFrame from './PhoneFrame';
import { LAYOUTS, cardLayout } from '../utils/theme';
import { getCardStats, leadCaptureOn } from '../utils/leads';
import CheckoutNotice from './sales/CheckoutNotice';
import BillingButton from './BillingButton';
import { hasPaidAccess, hasStripeBilling, planStatus } from '../utils/billing';
import { PlanChip, UpgradePanel } from './Upgrade';
import { BRAND } from '../config/brand';

/** / — the signed-in home page: your card(s) per organization. */
export default function Dashboard() {
  const { user, memberships, isPlatformAdmin } = useAuth();
  const [cards, setCards] = useState(null);
  const [themes, setThemes] = useState({});
  const [error, setError] = useState('');
  const [params] = useSearchParams();
  const [upgradeOrg, setUpgradeOrg] = useState(() => params.get('upgrade'));

  useEffect(() => {
    if (!memberships.length) return;
    let active = true;
    (async () => {
      try {
        const mine = await getMyCards(user.id);
        const orgIds = [...new Set(memberships.map((m) => m.org_id))];
        const loaded = await Promise.all(orgIds.map((id) => getTheme(id).then((t) => [id, t])));
        if (!active) return;
        setCards(mine);
        setThemes(Object.fromEntries(loaded));
      } catch (err) {
        console.error(err);
        if (active) setError('Could not load your cards. Please refresh the page.');
      }
    })();
    return () => { active = false; };
  }, [user.id, memberships]);

  if (!memberships.length) {
    return (
      <>
        <AppHeader />
        {isPlatformAdmin && <AdminWelcome />}
        <Onboarding />
      </>
    );
  }
  if (!cards && !error) return <Loading label="Loading your cards..." />;

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-12">
        {error && <div className={errorBox}>{error}</div>}
        <CheckoutNotice />
        {memberships.map((m) => {
          const org = m.organizations;
          const card = (cards || []).find((c) => c.org_id === org.id);
          const canManage = ['owner', 'admin'].includes(m.role);
          const canUpgrade = canManage && org.status === 'active' && planStatus(org).upgradable;
          const upgrading = upgradeOrg === org.id;
          const theme = themes[org.id];
          const layoutName = card && LAYOUTS.find((l) => l.id === cardLayout(card, theme))?.name;
          return (
            <section key={org.id} className="space-y-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="text-[34px] leading-10 font-extrabold tracking-tight">
                      {org.kind === 'personal' ? 'My card' : org.name}
                    </h1>
                    <PlanChip org={org} />
                    {card && <StatusChip status={card.status} />}
                  </div>
                  <p className="text-[15px] text-ink-muted mt-1">
                    {[org.kind === 'company' ? `Your role: ${m.role}` : null, layoutName ? `${layoutName} layout` : null].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {(card || canUpgrade) && (
                  <div className="flex flex-wrap gap-2.5">
                    {canUpgrade && !upgrading && (
                      <button type="button" onClick={() => setUpgradeOrg(org.id)}
                        className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark">
                        {planStatus(org).offer ? 'Add your team · 25% off' : `Upgrade to ${org.kind === 'company' ? 'Teams' : 'Pro'}`}
                      </button>
                    )}
                    {canManage && hasStripeBilling(org) && <BillingButton orgId={org.id} />}
                    {card && <a href={`/c/${card.slug}`} target="_blank" rel="noopener noreferrer" className={secondaryButton}>View public card</a>}
                    {card && <Link to={`/cards/${card.id}/edit`} className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark">Edit card</Link>}
                  </div>
                )}
              </div>

              {upgrading && canUpgrade && <UpgradePanel org={org} onCancel={() => setUpgradeOrg(null)} />}

              {card ? (
                <div className="flex flex-wrap gap-6 items-start">
                  <div className="w-full sm:w-[340px] shrink-0">
                    <PhoneFrame>
                      <CardDisplay card={card} theme={theme} className="min-h-[600px] [&>div]:min-h-[600px]" />
                    </PhoneFrame>
                  </div>
                  <div className="flex-1 min-w-[300px] flex flex-col gap-6">
                    <ShareTools card={card} theme={theme} />
                    <CardStats card={card} theme={theme} org={org} canManage={canManage} onUpgrade={() => setUpgradeOrg(org.id)} />
                    <NfcPromo cardId={card.id} />
                  </div>
                </div>
              ) : canManage ? (
                <EmptyState
                  text="You haven't created your card yet."
                  action={<Link to={`/cards/new/${org.id}`} className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark">Create my card</Link>}
                />
              ) : (
                <EmptyState text={`${org.name} hasn't set up your card yet. Your company admin will let you know when it's ready.`} />
              )}
            </section>
          );
        })}
      </main>
    </div>
  );
}

function CardStats({ card, theme, org, canManage, onUpgrade }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let active = true;
    getCardStats(card.id).then((s) => active && setStats(s)).catch(() => active && setStats({}));
    return () => { active = false; };
  }, [card.id]);
  const paid = hasPaidAccess(org);
  const n = (v) => (stats === null ? '…' : v == null ? '—' : v.toLocaleString());
  const saved = stats && stats.saves != null && stats.leads != null ? stats.saves + stats.leads : null;
  const asking = paid && leadCaptureOn(card, theme);
  const plan = org?.kind === 'company' ? 'Teams' : 'Pro';
  return (
    <section className="bg-white rounded-2xl border border-line p-6" aria-label="Card activity">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h2 className="text-[22px] font-bold">Last 30 days</h2>
        <Link to="/leads" className="text-sm font-semibold text-blue hover:underline">See leads</Link>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[['Views', n(stats?.views)], ['Contacts saved', n(saved)], ['Leads', paid ? n(stats?.leads) : null]].map(([label, value]) => (
          <div key={label} className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-semibold tracking-wider uppercase text-ink-muted">{label}</span>
            {value === null ? (
              <span className="inline-flex items-center gap-1.5 h-8 text-sm font-semibold text-ink-muted" title={`Leads are part of ${plan}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                {plan}
              </span>
            ) : (
              <span className="font-display font-bold text-[28px] leading-8">{value}</span>
            )}
          </div>
        ))}
      </div>
      <div className="text-sm text-ink-muted mt-4 pt-4 border-t border-line">
        {!paid ? (
          <p>
            Know who saved your contact: with {plan}, visitors are invited to share their name and email, and you get an email for every new lead.{' '}
            {canManage
              ? <button type="button" onClick={onUpgrade} className="font-semibold text-blue hover:underline">Upgrade to {plan}</button>
              : 'Ask your company admin to upgrade.'}
          </p>
        ) : asking
          ? 'Visitors are invited to share their details before saving your contact.'
          : 'Visitors save your contact directly. Turn on "Ask for contact details first" in the editor to collect leads.'}
      </div>
    </section>
  );
}

function StatusChip({ status }) {
  const live = status === 'active';
  return (
    <span className={`h-[26px] px-2.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-1.5 ${live ? 'bg-blue-soft text-blue' : 'bg-coral-soft text-coral-ink'}`}>
      <span className={`w-[7px] h-[7px] rounded-full ${live ? 'bg-blue' : 'bg-coral-ink'}`} />
      {live ? 'Live' : status === 'draft' ? 'Draft' : 'Suspended'}
    </span>
  );
}

function NfcPromo({ cardId }) {
  return (
    <section className="bg-navy text-white rounded-2xl p-6 flex flex-wrap items-center gap-6 overflow-hidden">
      <div className="w-[200px] aspect-[1.586] rounded-xl bg-blue p-3.5 relative -rotate-6 shadow-[0_14px_30px_rgba(0,0,0,0.35)] shrink-0" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 22 22" className="absolute right-3 top-3"><circle cx="7" cy="15" r="4" fill="#FF5E5B" /><path d="M7 5a10 10 0 0 1 10 10" fill="none" stroke="#FF5E5B" strokeWidth="2.4" strokeLinecap="round" /></svg>
        <img src={BRAND.logo.wordmarkWhite} alt="" className="h-4 w-auto absolute left-3.5 bottom-3" />
      </div>
      <div className="flex-1 min-w-[220px] space-y-2">
        <h2 className="text-[22px] font-bold">NFC cards are coming</h2>
        <p className="text-[15px] leading-[22px] text-[#C9D6E6]">A printed plastic card in your design. Tap it on any phone to open your digital card.</p>
        <Link to={`/cards/${cardId}/print`} className="inline-block text-sm font-semibold text-blue-light hover:text-white hover:underline">Preview your NFC card design →</Link>
      </div>
    </section>
  );
}

function EmptyState({ text, action }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-line-strong p-10 text-center">
      <p className="text-ink-muted mb-4">{text}</p>
      {action}
    </div>
  );
}

/**
 * First sign-in with no account yet. Open company invites for the person's email come first
 * (accept right here), so an invited employee never makes a personal account by mistake;
 * create_personal_organization refuses while an invite is open. Otherwise: a personal account.
 */
function Onboarding() {
  const { refreshProfile } = useAuth();
  const [invites, setInvites] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadInvites = async () => {
    const { data, error } = await supabase.rpc('my_pending_invites');
    if (error) console.error('Error loading invites:', error);
    setInvites(data || []);
  };
  useEffect(() => { loadInvites(); }, []);

  const accept = async (token) => {
    setError('');
    setLoading(true);
    const { error } = await supabase.rpc('accept_invite', { p_token: token });
    if (error) { setLoading(false); setError(error.message); loadInvites(); return; }
    await refreshProfile();
  };

  const create = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await supabase.rpc('create_personal_organization', { p_name: name.trim() });
    setLoading(false);
    if (error) {
      setError(error.message);
      if (error.hint === 'invite_pending') loadInvites();
      return;
    }
    await refreshProfile();
  };

  if (invites === null) return <main className="max-w-md mx-auto px-4 py-16 text-ink-muted">Loading...</main>;

  if (invites.length) {
    return (
      <main className="max-w-md mx-auto px-4 py-16">
        <h1 className="text-2xl font-bold text-ink mb-2">You're invited</h1>
        <p className="text-ink-muted mb-6">
          {invites.length === 1 ? `${invites[0].org_name} invited you` : 'These companies invited you'} to {BRAND.name}. Accept to get your card there.
        </p>
        <div className="space-y-3">
          {invites.map((i) => (
            <div key={i.token} className="bg-white rounded-2xl border border-line p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-ink">{i.org_name}</div>
                <div className="text-sm text-ink-muted">{i.role === 'admin' ? 'Company admin' : 'Employee'}</div>
              </div>
              <button onClick={() => accept(i.token)} disabled={loading} className={`${primaryButton} !w-auto px-5`}>
                {loading ? 'Joining...' : `Join ${i.org_name}`}
              </button>
            </div>
          ))}
        </div>
        {error && <div className={`${errorBox} mt-4`}>{error}</div>}
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-4 py-16">
      <h1 className="text-2xl font-bold text-ink mb-2">Welcome to {BRAND.name}</h1>
      <p className="text-ink-muted mb-6">
        Start with your own card. If your company invited you, open the link in your invitation email instead.
      </p>
      <form onSubmit={create} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-ink mb-1">Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required disabled={loading} />
        </label>
        {error && <div className={errorBox}>{error}</div>}
        <button type="submit" disabled={loading} className={primaryButton}>
          {loading ? 'Setting up...' : 'Set up my card'}
        </button>
      </form>
    </main>
  );
}

function AdminWelcome() {
  return (
    <main className="max-w-md mx-auto px-4 pt-12 text-center">
      <h1 className="text-2xl font-bold text-ink mb-2">You're a platform admin</h1>
      <p className="text-ink-muted mb-6">Create companies and user accounts from the admin console.</p>
      <Link to="/admin" className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark">Open admin console</Link>
    </main>
  );
}
